import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Editor, Toolbar } from 'ngx-editor';
import { MessageService } from '../../core/services/message.service';
import { Message, SendMessageRequest, SendMessageWithAttachmentsRequest } from '../../core/models/message';
import { DateTime } from 'luxon';
import { MatDialog } from '@angular/material/dialog';
import { DocumentDetailsDialogComponent } from '../../components/document-details-dialog/document-details-dialog.component';
import { MessageAttachment } from '../../core/models/message-document';
import { catchError, forkJoin, Observable, of, tap, throwError, map, take, ObservableLike } from 'rxjs';
import { ClientDocumentService, IClientDocumentUploadResult } from '../../core/services/client-document.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageListService } from '../../core/services/message-list.service';



@Component({
  selector: 'app-message-compose',
  templateUrl: './message-compose.component.html',
  styleUrl: './message-compose.component.scss',
})
export class MessageComposeComponent implements OnInit, OnDestroy {

matterId: string | undefined;

  form = new FormGroup({
    subject: new FormControl('', Validators.required),
    body: new FormControl('', Validators.required),
  });

  editor?: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic', 'underline'],
    ['ordered_list', 'bullet_list'],
  ];

  replyMessageId?: string;
  sending = true;
  attachments: MessageAttachment[] = [];
  loading: boolean = false;
  sent: boolean = false;
  errorMessage: any;
  returnUrl: string | null = null;

  constructor(private messageService: MessageService, private router: Router, private activatedRoute: ActivatedRoute, private dialog: MatDialog, private documentService: ClientDocumentService, private messageListService: MessageListService) {}

  ngOnInit(): void {


    this.returnUrl = this.activatedRoute.snapshot.queryParamMap.get('returnUrl');

    window.addEventListener('beforeunload', this.handleBeforeUnload);
    
     
    this.editor = new Editor();

    this.replyMessageId = this.activatedRoute.snapshot.queryParams['replyMessageId'];

    if (this.replyMessageId) {
      this.messageService.getMessage(this.replyMessageId).subscribe(
        message => {
          this.processReplyMessage(message);
          this.sending = false;
        },
        // error => this.errorMessage = error
      );
    }
    else {
      this.sending = false;
    }
  }

  ngOnDestroy(): void {
    this.editor?.destroy();

    window.removeEventListener('beforeunload', this.handleBeforeUnload);

  }

  handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (this.attachments.length > 0 && !this.sent) {
    event.preventDefault();
    event.returnValue = 'You have unsent attachments. Are you sure you want to leave?';
  }
  }

cancel() {

 this.returnToPreviousPage();
}

send() {
    this.sending = true;

    const timezoneRegex = new Date().toString().match(/([A-Z]+[\+-][0-9]+.*)/);
    const timeZone = timezoneRegex ? timezoneRegex[1] : '';
    let uniqueDocumentIds: string[] = [];
    if(this.attachments.length > 0) {
      uniqueDocumentIds = Array.from(
          new Set(this.attachments.map(att => att.messageDocument.id))
      );
     
      const messageRequest = {
        subject: this.form.value.subject!,
        body: this.form.value.body!,
        timeZone,
        replyToMessageId: this.replyMessageId,
        attachedDocumentIds: uniqueDocumentIds
      } as SendMessageWithAttachmentsRequest;

      this.messageService.sendMessageWithAttachments(messageRequest).subscribe(res => {
        this.sent = true;
        this.messageListService.refreshSentMessages();
        this.returnToPreviousPage();
      });

    }
    else{
    
    const messageRequest = {
      subject: this.form.value.subject!,
      body: this.form.value.body!,
      timeZone,
      replyToMessageId: this.replyMessageId
   } as SendMessageRequest;

    this.messageService.sendMessage(messageRequest).subscribe(res => {
      this.sent = true;
      this.messageListService.refreshSentMessages();
      this.returnToPreviousPage();
    });
  }
}

private returnToPreviousPage() {
  if (this.returnUrl) {
    this.router.navigate([this.returnUrl]);
  } else {
    this.router.navigate(['../home']);
  }
}

  private processReplyMessage(message: Message) {
    const subject = message.subject.startsWith("RE:")
      ? message.subject
      : 'RE: ' + message.subject;

    let staffName = message.staffName
      ? message.staffName
      : '';

    let blankLine = '<br/>'

    const body = blankLine +
      blankLine +
      '<hr />' +
      '<p><strong>From:&nbsp;</strong>' + staffName + '</p>' + 
      '<p><strong>Sent:&nbsp;</strong>' + this.formatDateForReply(message.timeSent) + '</p>' + 
      '<p><strong>Subject:&nbsp;</strong>' + message.subject + '</p>' + 
      blankLine +
      message.body;

      this.form.controls.subject.setValue(subject);
      this.form.controls.body.setValue(body);
  }

  private formatDateForReply = (date: string) => {
    if (!date) {
      return '';
    }

    const LocalTime =  `${DateTime.fromISO(date).toLocal().toFormat('MMM d, yyyy HH:mm Z')}`;
    const UTCTime =  `(UTC: ${DateTime.fromISO(date).toUTC().toISO()})`;

    return `${LocalTime} ${UTCTime}`;
  }

    addAttachment() {

     
          const data = {
          document: null,
          documents: null,
          matterId: this.matterId ?? null,
          documentSelection: [],
          isMessageAttachment: true,
        }

        this.openUploadDialog(data);
    
     
  }

    openUploadDialog(data: any){
        const dialogRef = this.dialog.open(DocumentDetailsDialogComponent, {
        data: data,
        panelClass: 'dialog-panel-large'
        });

         dialogRef.componentInstance.itemsUploaded?.subscribe((data: IClientDocumentUploadResult) => {
        
        this.attachments.push({
          messageDocument: {
          id: data.document.id,
          fileDescription: data.document.fileDesc ? data.document.fileDesc : data.document.fileName,
          showInPortal: false,
          markForDelete: false,
          useId: false,
          fileSize: data.document.fileSize ?? 0
          },
          files: data.files
          } as MessageAttachment);
        });

   
    }

deleteAttachment(documentId: string) {
  this.documentService.deleteDocument(documentId).pipe(catchError(error => this.handleAttachmentDeleteError(error, documentId))).subscribe({
    next: () => {
           this.attachments = this.attachments.filter(att => att.messageDocument.id !== documentId);
    },
    error: (error: Error) => {
      this.errorMessage = error.message;
    }
  });
}

deleteUnsentAttachments() {
  const uniqueDocumentIds = Array.from(new Set(this.attachments.map(att => att.messageDocument.id)));

  if (uniqueDocumentIds.length > 0) {
    const deleteCalls = uniqueDocumentIds.map(id =>
      this.documentService.deleteDocument(id).pipe(catchError(error => this.handleAttachmentDeleteError(error, id)))
    );

    forkJoin(deleteCalls).subscribe({
      next: () => {
        // All deletions complete
        this.attachments = [];
      },
      error: (error: Error) => {
        this.errorMessage = error.message
        this.attachments = [];
      }
    });
  } 
}

// observable that deletes all unsent attachments and completes when all deletions are done.
// for use in CanDeactivate guard
deleteUnsentAttachments$(): Observable<boolean> {
  const uniqueDocumentIds = Array.from(new Set(this.attachments.map(att => att.messageDocument.id)));

  if (uniqueDocumentIds.length > 0) {
    const deleteCalls = uniqueDocumentIds.map(id =>
      this.documentService.deleteDocument(id).pipe(
        take(1),
        map(() => true),
        catchError(error => {
          this.handleAttachmentDeleteError(error, id);
          return of(null); // ignore error, emit null
        })
      )
    );
    return forkJoin(deleteCalls).pipe(
      map(() => true),
      catchError(() => of(true)) // ensure true even if forkJoin errors
    );
  }
  return of(true);
}

private handleAttachmentDeleteError(error: HttpErrorResponse, documentId: string) {
  if (error.status === 0) {
    // A client-side or network error occurred. Handle it accordingly.
    console.error(`An error occurred deleting attachment with id: ${documentId}:`, error.error);
  } else {
    // The backend returned an unsuccessful response code.
    // The response body may contain clues as to what went wrong.
    console.error(
      `Backend returned code ${error.status}, body was: `, error.error);
  }
  // Return an observable with a user-facing error message.
  return throwError(() => new Error(`Error deleting attachment. Error: ${error.status} ${error.statusText}`));
}

canDeactivate(): boolean | Observable<boolean> {

  if (this.attachments.length > 0 && !this.sent) {
    var confirm = window.confirm('You have unsent attachments. Are you sure you want to leave?');
    if (!confirm) {
      return false;
    }
    return this.deleteUnsentAttachments$();
  }
  return true;
}

}




